import React, { useCallback, useContext, useEffect, useState } from "react";
import { Divider } from "@mui/material";
import useStyles from "style/js-style/containers/administration/roles/privileges/ScopeDropStyle";
import EAIcon from "components/common/EAIcon";
import i18next from "i18next";
import { useFormContext } from "react-hook-form";
import _ from "lodash";
import {
  getContextCriterionOptions,
  getDossierCriterionOptions,
  getThirdPartyCriterionOptions,
} from 'services/common/RolesUtils';
import { updateRoleScopes } from "services/edge/RoleService";
import { getAccountRequirements } from "services/edge/AccountService";
import { ERROR, FREE_DOSSIER_TYPE_ID_VALUE, SUCCESS } from "services/common/Constants";
import { Context } from "states/Store";
import RoleScopeForm from "../../form/RoleScopeForm";
import ScopeCriteriaFormFields from "../../form/criteria/ScopeCriteriaFormFields";
import AuthorizedDocumentsFormSelect from "../../form/criteria/AuthorizedDocumentsFormSelect";
import { getDoctypeTranslatedName } from '../../../../../services/common/DocumentUtils';
import { getContextInstances, getContextModel } from '../../../../../services/edge/ContexteService';

const FullScopeTitle = ({ type }) => {
  const { classes } = useStyles();

  return (
    <div className={classes.titleWrapper}>
      <span className={classes.titleMain}>{i18next.t(`roles.scope.${type}.title`)}</span>
      <span className={classes.titleAllPart}> - {i18next.t(`all`)} </span>
      <span className={classes.titleSubTitle}>&nbsp;({i18next.t(`roles.scope.${type}.subtitle`)})</span>
    </div>
  )
}

const ScopeFormFieldsWrapper = ({ setSubmitHandler, role, onValidateSuccess, type, accountAttributes, accountCountries, accountActivities, accountCalculatedAttributes, accountIndicators, qualifyingCriteriaSelectedrole }) => {
  const formMethods = useFormContext();
  const { handleSubmit } = formMethods;
  const [state, dispatch] = useContext(Context);

  const [dossierTypes, setDossierTypes] = useState();

  const [thirdPartyCriterionOptions, setThirdPartyCriterionOptions] = useState(null);
  const [dossierCriterionOptions, setDossierCriterionOptions] = useState(null);
  const [contextCriterionOptions, setContextCriterionOptions] = useState(null);
  const [documentTypes, setDocumentTypes] = useState();

  // Formate dossierType
  useEffect(() => {
    const mappedDossierTypes = [
      {
        value: `${FREE_DOSSIER_TYPE_ID_VALUE}`,
        label: i18next.t("free")
      }
    ];
    if (state.dossierTypes && !_.isEmpty(state.dossierTypes)) {
      state.dossierTypes.forEach((dt) => {
        mappedDossierTypes.push({
          value: dt.code,
          label: dt.libelle
        });
      });
    }

    setDossierTypes(mappedDossierTypes);
  }, [state.dossierTypes]);

  useEffect(() => {
    if (accountCountries && accountActivities && accountAttributes && accountCalculatedAttributes && accountIndicators) {
      setThirdPartyCriterionOptions(getThirdPartyCriterionOptions(accountCountries, accountActivities,
        accountAttributes, accountCalculatedAttributes, accountIndicators, state));
    }
  }, [accountAttributes, accountCountries, accountActivities, accountCalculatedAttributes, accountIndicators, state]);

  useEffect(() => {
    if (dossierTypes && accountAttributes) {
      setDossierCriterionOptions(getDossierCriterionOptions(dossierTypes, accountAttributes));
    }
  }, [dossierTypes, accountAttributes]);

  // Fetch document types
  useEffect(() => {
    if (state.account && state.doctypeTraductions) {
      (async function fetchData() {
        try {
          const results = await getAccountRequirements(state.account.id, dispatch);

          setDocumentTypes(
            results.content.map((x) => {
              return {
                value: x.documentId,
                label: getDoctypeTranslatedName(state.doctypeTraductions, x.code, state.lang) || x.descriptionFr
              }
            })
          );
        } catch (error) { }
      })();
    }
  }, [state.account, state.doctypeTraductions, state.lang, dispatch]);

  // Fetch context models
  useEffect(() => {
    const fetchModels = async () => {
      const models = await getContextModel(state.account.id, dispatch);
      const parsedModels = models.map((m) => {
        return {
          value: JSON.stringify(m.id),
          label: m.name
        }
      });
      const instances = await getContextInstances(state.account.id, true, dispatch);
      const parsedInstances = instances.content.map((instance) => {
        return {
          value: JSON.stringify(instance.id),
          label: instance.reference,
        }
      })
      const attributes = models.flatMap((m) => m.contextAttributes);
      setContextCriterionOptions(getContextCriterionOptions(parsedModels, parsedInstances, attributes));
    }
    fetchModels();
  }, [state.account, dispatch])

  // Save
  const submitHandler = useCallback(() => {
    const submitForm = async (data) => {
      try {
        await updateRoleScopes(
          state.account.id,
          role.id,
          data = {
            ...data,
            thirdPartyQualifyingCriteria: data.thirdPartyQualifyingCriteria.filter((x) => x.field !== "" && x.field !== undefined),
            dossierQualifyingCriteria: data.dossierQualifyingCriteria.filter((x) => x.field !== "" && x.field !== undefined),
            contextQualifyingCriteria: data.contextQualifyingCriteria.filter((x) => x.field !== "" && x.field !== undefined),
          },
          dispatch
        );
        dispatch({
          type: "ALERT",
          alert: {
            type: SUCCESS,
            msg: "Les modifications ont bien été enregistrées.",
          },
        });

        onValidateSuccess();
      } catch (error) {
        dispatch({
          type: "ALERT",
          alert: {
            type: ERROR,
            msg: "Erreur interne.",
          },
        });
      }
    }
    handleSubmit(submitForm)();
  }, [handleSubmit, dispatch, onValidateSuccess, state.account.id, role]);

  useEffect(() => {
    setSubmitHandler(() => submitHandler);
  }, [submitHandler, setSubmitHandler])


  switch (type) {
    case "thirdparty":
      return (
        <ScopeCriteriaFormFields
          criterionFieldOptions={thirdPartyCriterionOptions}
          fieldName={"thirdPartyQualifyingCriteria"}
          readOnlyQualifyingCriteria={qualifyingCriteriaSelectedrole.thirdPartyQualifyingCriteria}
        />
      )
    case "dossier":
      return (
        <ScopeCriteriaFormFields
          criterionFieldOptions={dossierCriterionOptions}
          fieldName={"dossierQualifyingCriteria"}
          readOnlyQualifyingCriteria={qualifyingCriteriaSelectedrole.dossierQualifyingCriteria}
        />
      )
    case "document":
      return (
        <AuthorizedDocumentsFormSelect
          documentTypes={documentTypes}
          fieldName={"authorizedDocumentTypes"}
          readOnlyQualifyingCriteria={qualifyingCriteriaSelectedrole.authorizedDocumentTypes}
        />
      )
    case "context":
      return (
        <ScopeCriteriaFormFields
          criterionFieldOptions={contextCriterionOptions}
          fieldName={"contextQualifyingCriteria"}
          readOnlyQualifyingCriteria={qualifyingCriteriaSelectedrole.contextQualifyingCriteria}
        />
      )
    default: return;
  }

}

function RoleScopes({ 
  setSubmitHandler, 
  onValidateSuccess, 
  role, 
  accountActivities, 
  accountCountries, 
  accountAttributesAndIndicators,
  qualifyingCriteriaSelectedrole
}) {
  const { classes } = useStyles();

  const [thirdPartyAccordionIsOpen, setThirdPartyAccordionIsOpen] = useState(true);
  const [dossierAccordionIsOpen, setDossierAccordionIsOpen] = useState(true);
  const [documentAccordionIsOpen, setDocumentAccordionIsOpen] = useState(true);
  const [contextAccordionIsOpen, setContextAccordionIsOpen] = useState(true);

  return (
    <RoleScopeForm submitHandler={() => { }} formId={"scope-form-id"} role={role}>
      {/* ThirdParties */}
      <div className={classes.root}>
        <EAIcon icon="arrow/arrow-to-up_large" className={thirdPartyAccordionIsOpen ? classes.arrowIcon : classes.arrowIconReverse} />
        <div className={classes.headerWrapper} onClick={() => setThirdPartyAccordionIsOpen(!thirdPartyAccordionIsOpen)}>
          <FullScopeTitle type={"thirdparty"} />
        </div>
        {/* Content */}
        {thirdPartyAccordionIsOpen &&
          <div className={classes.subItemsWrapper}>
            <Divider />
            {/* Form */}
            <div className={classes.formWrapper}>
              <div className={classes.formTitleDeco}></div>
              <div className={classes.formTitle}> {i18next.t(`roles.scope.thirdparty.title`)} </div>
              <div className={classes.formLine}>
                <ScopeFormFieldsWrapper
                  setSubmitHandler={setSubmitHandler}
                  role={role}
                  onValidateSuccess={onValidateSuccess}
                  type={"thirdparty"}
                  accountActivities={accountActivities}
                  accountCountries={accountCountries}
                  accountAttributes={accountAttributesAndIndicators.thirdpartyAttributes}
                  accountIndicators={accountAttributesAndIndicators.thirdpartyIndicators}
                  accountCalculatedAttributes={accountAttributesAndIndicators.thirdpartyCalculatedAttributes}
                  qualifyingCriteriaSelectedrole={qualifyingCriteriaSelectedrole}
                />
              </div>
            </div>
          </div>
        }
      </div>

      {/* Dossiers */}
      <div className={classes.root}>
        <EAIcon icon="arrow/arrow-to-up_large" className={dossierAccordionIsOpen ? classes.arrowIcon : classes.arrowIconReverse} />
        <div className={classes.headerWrapper} onClick={() => setDossierAccordionIsOpen(!dossierAccordionIsOpen)}>
          <FullScopeTitle type={"dossier"} />
        </div>
        {/* Content */}
        {dossierAccordionIsOpen &&
          <div className={classes.subItemsWrapper}>
            <Divider />
            {/* Form */}
            <div className={classes.formWrapper}>
              <div className={classes.formTitleDeco}></div>
              <div className={classes.formTitle}> {i18next.t(`roles.scope.dossier.title`)} </div>
              <div className={classes.formLine}>
                <ScopeFormFieldsWrapper
                  setSubmitHandler={setSubmitHandler}
                  role={role}
                  onValidateSuccess={onValidateSuccess}
                  type={"dossier"}
                  accountAttributes={accountAttributesAndIndicators.dossierAttributes}
                  qualifyingCriteriaSelectedrole={qualifyingCriteriaSelectedrole}
                />
              </div>
            </div>
          </div>
        }
      </div>

      {/* Documents */}
      <div className={classes.root}>
        <EAIcon icon="arrow/arrow-to-up_large" className={documentAccordionIsOpen ? classes.arrowIcon : classes.arrowIconReverse} />
        <div className={classes.headerWrapper} onClick={() => setDocumentAccordionIsOpen(!documentAccordionIsOpen)}>
          <FullScopeTitle type={"document"} />
        </div>
        {/* Content */}
        {documentAccordionIsOpen &&
          <div className={classes.subItemsWrapper}>
            <Divider />
            {/* Form */}
            <div className={classes.formWrapper}>
              <div className={classes.formTitleDeco}></div>
              <div className={classes.formTitle}> {i18next.t(`roles.scope.document.title`)} </div>
              <div className={classes.formLine} style={{ paddingTop: "16px" }}>
                <ScopeFormFieldsWrapper
                  setSubmitHandler={setSubmitHandler}
                  role={role}
                  onValidateSuccess={onValidateSuccess}
                  type={"document"}
                  accountCountries={accountCountries}
                  qualifyingCriteriaSelectedrole={qualifyingCriteriaSelectedrole}
                />
              </div>
            </div>
          </div>
        }
      </div>

      {/* Context */}
      <div className={classes.root}>
        <EAIcon icon="arrow/arrow-to-up_large" className={contextAccordionIsOpen ? classes.arrowIcon : classes.arrowIconReverse} />
        <div className={classes.headerWrapper} onClick={() => setContextAccordionIsOpen(!contextAccordionIsOpen)}>
          <FullScopeTitle type={"context"} />
        </div>
        {/* Content */}
        {contextAccordionIsOpen &&
          <div className={classes.subItemsWrapper}>
            <Divider />
            {/* Form */}
            <div className={classes.formWrapper}>
              <div className={classes.formTitleDeco}></div>
              <div className={classes.formTitle}> {i18next.t(`roles.scope.context.title`)} </div>
              <div className={classes.formLine}>
                <ScopeFormFieldsWrapper
                  setSubmitHandler={setSubmitHandler}
                  role={role}
                  onValidateSuccess={onValidateSuccess}
                  type={"context"}
                  accountAttributes={accountAttributesAndIndicators.dossierAttributes}
                  qualifyingCriteriaSelectedrole={qualifyingCriteriaSelectedrole}
                />
              </div>
            </div>
          </div>
        }
      </div>
    </RoleScopeForm>
  )
}

export default RoleScopes;
